import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Zap, Shield, Clock, TrendingDown, ChevronDown, Sparkles } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const SOURCE_MODELS = [
  { id: "gpt-4o", label: "GPT-4o" },
  { id: "gpt-4-turbo", label: "GPT-4 Turbo" },
  { id: "gpt-4o-mini", label: "GPT-4o Mini" },
  { id: "o1", label: "o1" },
  { id: "gpt-3.5-turbo", label: "GPT-3.5 Turbo" },
];

function SavingsBar({ pct }) {
  const clamped = Math.max(0, Math.min(pct, 100));
  const color = clamped > 70 ? "bg-emerald-500" : clamped > 40 ? "bg-[var(--qn-primary)]" : clamped > 0 ? "bg-blue-500" : "bg-red-400";
  return (
    <div className="w-full h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${clamped}%` }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`h-full rounded-full ${color}`}
      />
    </div>
  );
}

function ComparisonRow({ alt, index }) {
  const savingsColor = alt.savings_vs_source_pct > 70 ? "text-emerald-400" : alt.savings_vs_source_pct > 30 ? "text-[var(--qn-primary)]" : alt.savings_vs_source_pct > 0 ? "text-blue-400" : "text-red-400";
  const latencyColor = alt.latency.delta_vs_source_ms < -50 ? "text-emerald-400" : alt.latency.delta_vs_source_ms < 50 ? "text-[var(--qn-foreground-muted)]" : "text-amber-400";

  return (
    <motion.tr
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className={`border-b border-[var(--qn-border)]/30 transition-colors ${
        alt.is_source ? "bg-white/[0.03]" : "hover:bg-white/[0.02]"
      }`}
    >
      <td className="py-3 pl-4 pr-3">
        <div className="flex items-center gap-2">
          {alt.is_source && <span className="text-[9px] px-1 py-0.5 rounded bg-white/10 text-[var(--qn-foreground-subtle)]">SOURCE</span>}
          <div>
            <div className="text-sm font-medium text-[var(--qn-foreground)] truncate max-w-[200px]">{alt.display_name}</div>
            <div className="text-[10px] text-[var(--qn-foreground-subtle)]">{alt.provider}</div>
          </div>
        </div>
      </td>
      <td className="py-3 pr-3 text-center">
        <div className="inline-flex items-center gap-1">
          <Shield className="w-3 h-3 text-[var(--qn-foreground-muted)]" />
          <span className="text-sm font-semibold tabular-nums text-[var(--qn-foreground)]">{alt.quality_score}</span>
          {alt.quality_delta !== 0 && (
            <span className={`text-[10px] ${alt.quality_delta > 0 ? "text-emerald-400" : "text-amber-400"}`}>
              {alt.quality_delta > 0 ? "+" : ""}{alt.quality_delta}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 pr-3 text-right">
        <span className="text-sm tabular-nums text-[var(--qn-foreground)]">${alt.cost.total.toFixed(6)}</span>
        <div className="text-[10px] text-[var(--qn-foreground-subtle)]">${alt.cost.per_million_input}/{alt.cost.per_million_output}</div>
      </td>
      <td className="py-3 pr-3 text-right w-[120px]">
        {!alt.is_source && (
          <div className="space-y-1">
            <span className={`text-sm font-semibold tabular-nums ${savingsColor}`}>
              {alt.savings_vs_source_pct > 0 ? "+" : ""}{alt.savings_vs_source_pct}%
            </span>
            <SavingsBar pct={alt.savings_vs_source_pct} />
          </div>
        )}
      </td>
      <td className="py-3 pr-4 text-right">
        <span className={`text-sm tabular-nums ${latencyColor}`}>{alt.latency.p50_ms}ms</span>
        {!alt.is_source && alt.latency.delta_vs_source_ms !== 0 && (
          <div className={`text-[10px] ${latencyColor}`}>
            {alt.latency.delta_vs_source_ms > 0 ? "+" : ""}{alt.latency.delta_vs_source_ms}ms
          </div>
        )}
      </td>
    </motion.tr>
  );
}

export default function ComparisonWidget({ compact = false }) {
  const [sourceModel, setSourceModel] = useState("gpt-4o");
  const [tokens, setTokens] = useState(1000);
  const [minQuality, setMinQuality] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/providers/compare`, { params: { model: sourceModel, tokens, min_quality: minQuality } })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [sourceModel, tokens, minQuality]);

  return (
    <div className="space-y-4" data-testid="comparison-widget" data-automation-id="comparison-widget">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-[var(--qn-primary)]" />
          <h3 className="text-lg font-semibold text-[var(--qn-foreground)]">Real-Time Provider Comparison</h3>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--qn-foreground-muted)]">Compare:</span>
          <select
            value={sourceModel}
            onChange={e => setSourceModel(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
            data-testid="compare-source-select"
            data-automation-id="compare-source-select"
          >
            {SOURCE_MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--qn-foreground-muted)]">Tokens:</span>
          <select
            value={tokens}
            onChange={e => setTokens(Number(e.target.value))}
            className="px-3 py-2 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
            data-testid="compare-tokens-select"
          >
            <option value={100}>100</option>
            <option value={1000}>1K</option>
            <option value={10000}>10K</option>
            <option value={100000}>100K</option>
            <option value={1000000}>1M</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--qn-foreground-muted)]">Min Quality:</span>
          <select
            value={minQuality}
            onChange={e => setMinQuality(Number(e.target.value))}
            className="px-3 py-2 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
            data-testid="compare-quality-select"
          >
            <option value={0}>Any</option>
            <option value={70}>70+</option>
            <option value={80}>80+</option>
            <option value={85}>85+</option>
            <option value={90}>90+</option>
          </select>
        </div>
      </div>

      {/* Recommendation card */}
      {data?.recommended && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.04]"
          data-testid="comparison-recommendation"
        >
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-4 h-4 text-emerald-400" />
            <span className="text-sm font-semibold text-emerald-400">QNNEAL Recommendation</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm text-[var(--qn-foreground)]">
              <strong>{data.source.display_name}</strong>
              <span className="text-[var(--qn-foreground-muted)]"> ({data.source.provider})</span>
            </span>
            <ArrowRight className="w-4 h-4 text-emerald-400" />
            <span className="text-sm text-[var(--qn-foreground)]">
              <strong>{data.recommended.display_name}</strong>
              <span className="text-[var(--qn-foreground-muted)]"> ({data.recommended.provider})</span>
            </span>
            <span className="text-sm font-bold text-emerald-400">
              {data.recommended.savings_vs_source_pct > 0 ? `${data.recommended.savings_vs_source_pct}% savings` : ""}
            </span>
          </div>
          <div className="flex items-center gap-4 mt-2 text-xs text-[var(--qn-foreground-muted)]">
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" /> Quality: {data.recommended.quality_score}/100
              ({data.recommended.quality_delta >= 0 ? "+" : ""}{data.recommended.quality_delta})
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> Latency: {data.recommended.latency.p50_ms}ms
              ({data.recommended.latency.delta_vs_source_ms > 0 ? "+" : ""}{data.recommended.latency.delta_vs_source_ms}ms)
            </span>
            <span className="flex items-center gap-1">
              <TrendingDown className="w-3 h-3" /> Cost: ${data.recommended.cost.total.toFixed(6)} vs ${data.source.cost_total.toFixed(6)}
            </span>
          </div>
        </motion.div>
      )}

      {/* Comparison table */}
      {loading ? (
        <div className="text-center py-8 text-sm text-[var(--qn-foreground-muted)]">Comparing models...</div>
      ) : data && (
        <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--qn-border)] text-xs text-[var(--qn-foreground-muted)]">
                  <th className="text-left py-2.5 pl-4 pr-3 font-medium">Model</th>
                  <th className="text-center py-2.5 pr-3 font-medium">Quality</th>
                  <th className="text-right py-2.5 pr-3 font-medium">Cost ({data.estimated_tokens.toLocaleString()} tok)</th>
                  <th className="text-right py-2.5 pr-3 font-medium">Savings</th>
                  <th className="text-right py-2.5 pr-4 font-medium">Latency (p50)</th>
                </tr>
              </thead>
              <tbody>
                {(compact ? data.alternatives.slice(0, 10) : data.alternatives).map((alt, i) => (
                  <ComparisonRow key={`${alt.provider_slug}-${alt.model_id}-${i}`} alt={alt} index={i} />
                ))}
              </tbody>
            </table>
          </div>
          {!compact && (
            <div className="px-4 py-2 border-t border-[var(--qn-border)] text-xs text-[var(--qn-foreground-subtle)]">
              Showing {data.alternatives.length} of {data.total_alternatives} alternatives. Max savings: {data.max_savings_pct}%
            </div>
          )}
        </div>
      )}
    </div>
  );
}
